import { useLazyQuery } from "@apollo/client";
import { useRef } from "react";
import { GET_ME } from "../lib/user/queries";
import { handleNonAdminUser, canAccessStaffPortal } from "../lib/user/validation";
import { getToken } from "../lib/actions";
import { clearApolloCache } from "../lib/apollo";
import type { UserObjectType } from "../types/schema";

interface UseUserQueryOptions {
  onUserFetched?: (user: UserObjectType | null) => void;
  onError?: (error: string | null) => void;
  onLoadingChange?: (loading: boolean) => void;
}

export const useUserQuery = (options: UseUserQueryOptions = {}) => {
  const { onUserFetched, onError, onLoadingChange } = options;
  const isFetchingRef = useRef(false);

  const [fetchMe, { loading, error }] = useLazyQuery(GET_ME, {
    fetchPolicy: "cache-first",
    errorPolicy: "all",
    onCompleted: async data => {
      if (data?.me) {
        if (canAccessStaffPortal(data.me)) {
          onUserFetched?.(data.me);
        } else {
          await handleNonAdminUser(data.me);
          return;
        }
      } else {
        onUserFetched?.(null);
      }
      isFetchingRef.current = false;
    },
    onError: error => {
      console.error("Error fetching user:", error);
      onError?.(error.message);
      onUserFetched?.(null);
      isFetchingRef.current = false;
    },
  });

  const executeQuery = async (fetchPolicy?: "cache-first" | "network-only") => {
    if (isFetchingRef.current) return;

    try {
      isFetchingRef.current = true;
      onLoadingChange?.(true);
      onError?.(null);

      const token = await getToken();
      if (!token?.accessToken) {
        onUserFetched?.(null);
        onLoadingChange?.(false);
        isFetchingRef.current = false;
        return;
      }

      if (fetchPolicy === "network-only") {
        clearApolloCache();
      }

      await fetchMe({ fetchPolicy });
    } catch (error) {
      console.error("Error in executeQuery:", error);
      onError?.("Failed to fetch user data");
      onLoadingChange?.(false);
      isFetchingRef.current = false;
    }
  };

  const fetchUser = () => executeQuery("cache-first");
  const fetchUserFresh = () => executeQuery("network-only");

  const fetchUserBackground = async () => {
    if (isFetchingRef.current) return;

    try {
      isFetchingRef.current = true;
      onError?.(null);

      const token = await getToken();
      if (!token?.accessToken) {
        onUserFetched?.(null);
        isFetchingRef.current = false;
        return;
      }

      await fetchMe({ fetchPolicy: "network-only" });
    } catch (error) {
      console.warn("Background refresh failed:", error);
      isFetchingRef.current = false;
    }
  };

  return {
    fetchUser,
    fetchUserFresh,
    fetchUserBackground,
    loading,
    error,
    isFetching: isFetchingRef.current,
  };
};
